import Navbar from '../components/Navbar'
import { Link } from 'react-router-dom'
import SEO from '../components/SEO'

const TOC = [
  { id: 'overview',        label: 'Overview' },
  { id: 'requirements',    label: 'DMCA notice requirements' },
  { id: 'submit',          label: 'How to submit a notice' },
  { id: 'counter-notice',  label: 'Counter-notices' },
  { id: 'repeat',          label: 'Repeat infringers' },
  { id: 'good-faith',      label: 'Good-faith obligations' },
  { id: 'fair-use',        label: 'Fair use & fan works' },
  { id: 'contact',         label: 'Contact the DMCA agent' },
]

export default function DmcaPage() {
  return (
    <div className="legal-page">
      <SEO title="DMCA Policy" description="Verse DMCA policy — how to report copyright infringement and our takedown procedures." />
      <Navbar />
      <div className="legal-page__inner">
        <header className="legal-page__header">
          <p className="legal-page__label">Legal</p>
          <h1 className="legal-h1">DMCA Takedown Policy</h1>
          <p className="legal-page__meta">Last updated: March 2026 · Effective: March 18, 2026</p>
        </header>

        <nav className="legal-toc" aria-label="Table of contents">
          <p className="legal-toc__heading">On this page</p>
          <ol className="legal-toc__list">
            {TOC.map((item) => (
              <li key={item.id}>
                <a href={`#${item.id}`} className="legal-toc__link">{item.label}</a>
              </li>
            ))}
          </ol>
        </nav>

        <section id="overview" className="legal-section">
          <h2 className="legal-h2">Overview</h2>
          <p className="legal-p">
            Verse respects the intellectual property rights of creators everywhere. We comply with the Digital
            Millennium Copyright Act (DMCA), 17 U.S.C. § 512, and respond promptly to valid takedown notices.
          </p>
          <p className="legal-p">
            If you believe that content posted on Verse infringes your copyright, you may submit a DMCA
            takedown notice using the process described below. You can also use our{' '}
            <Link to="/report" className="legal-link">in-platform reporting tool</Link> to flag potentially
            infringing stories directly from any story page.
          </p>
          <div className="legal-callout">
            <ul className="legal-callout__list">
              <li>Verse hosts user-generated content and is a service provider under 17 U.S.C. § 512(c).</li>
              <li>We do not pre-screen user content for copyright compliance.</li>
              <li>Valid DMCA notices are processed within <strong>5 business days</strong>.</li>
              <li>Submitting a false notice may expose you to legal liability under 17 U.S.C. § 512(f).</li>
            </ul>
          </div>
        </section>

        <section id="requirements" className="legal-section">
          <h2 className="legal-h2">DMCA notice requirements</h2>
          <p className="legal-p">
            To be valid under the DMCA, your written notice must include <strong>all</strong> of the following:
          </p>
          <ol className="legal-ol">
            <li>
              <strong>Your physical or electronic signature.</strong> The signature of the copyright owner or
              a person authorised to act on their behalf.
            </li>
            <li>
              <strong>Identification of the copyrighted work.</strong> A description of the work you claim has
              been infringed. If multiple works are covered by a single notice, a representative list is acceptable.
            </li>
            <li>
              <strong>Identification of the infringing material.</strong> A description of the material you believe
              infringes your work, with enough detail to allow us to locate it — ideally the full URL of the story
              or chapter on Verse.
            </li>
            <li>
              <strong>Your contact information.</strong> Your address, telephone number, and email address.
            </li>
            <li>
              <strong>A good-faith belief statement.</strong> A statement that you have a good-faith belief that
              use of the material in the manner complained of is not authorised by the copyright owner, its agent,
              or the law.
            </li>
            <li>
              <strong>An accuracy statement.</strong> A statement, made under penalty of perjury, that the
              information in your notice is accurate and that you are the copyright owner or are authorised to
              act on the copyright owner's behalf.
            </li>
          </ol>
          <p className="legal-p">
            Notices that are incomplete or materially deficient may not receive a response or may be treated as
            non-compliant.
          </p>
        </section>

        <section id="submit" className="legal-section">
          <h2 className="legal-h2">How to submit a notice</h2>
          <p className="legal-p">You can submit a DMCA notice in two ways:</p>

          <h3 className="legal-h3">Option 1 — In-platform report (fastest)</h3>
          <p className="legal-p">
            Click the <strong>Report</strong> button on any story page and select "Copyright violation."
            Provide the URL of your original work and any relevant details. Our moderation team will review
            the report within 5 business days.
          </p>

          <h3 className="legal-h3">Option 2 — Email our DMCA agent</h3>
          <p className="legal-p">
            Send a written notice containing all required elements listed above to our designated DMCA agent:
          </p>
          <div className="legal-contact-card">
            <div className="legal-contact-card__row">
              <span className="legal-contact-card__label">Designated Agent</span>
              <span>Zan Husain, Verse</span>
            </div>
            <div className="legal-contact-card__row">
              <span className="legal-contact-card__label">Mailing Address</span>
              <span>PO BOX 1101, CORAOPOLIS, PA 15108</span>
            </div>
            <div className="legal-contact-card__row">
              <span className="legal-contact-card__label">Email</span>
              <a href="mailto:support@versesite.com" className="legal-link">support@versesite.com</a>
            </div>
            <div className="legal-contact-card__row">
              <span className="legal-contact-card__label">Subject line</span>
              <span><code>DMCA Takedown Notice — [Story Title]</code></span>
            </div>
          </div>
          <p className="legal-p">
            Upon receipt of a valid notice, Verse will: (1) remove or disable access to the allegedly infringing
            content; (2) notify the user who posted the content; and (3) provide the user with a copy of the
            notice (with your personal contact details redacted where legally permissible).
          </p>
        </section>

        <section id="counter-notice" className="legal-section">
          <h2 className="legal-h2">Counter-notices</h2>
          <p className="legal-p">
            If you believe your content was removed as a result of mistake or misidentification, you may submit
            a counter-notice to our DMCA agent. A valid counter-notice must include:
          </p>
          <ol className="legal-ol">
            <li>Your physical or electronic signature.</li>
            <li>Identification of the content that was removed and the URL where it appeared before removal.</li>
            <li>
              A statement under penalty of perjury that you have a good-faith belief that the content was
              removed as a result of mistake or misidentification.
            </li>
            <li>
              Your name, address, telephone number, and email address, and a statement that you consent to
              the jurisdiction of the Federal District Court for the judicial district in which your address
              is located (or, if outside the United States, any judicial district in which Verse may be found).
            </li>
          </ol>
          <p className="legal-p">
            Upon receipt of a valid counter-notice, Verse will forward it to the original complainant and restore
            the content within <strong>10–14 business days</strong> unless the complainant files a court action
            seeking to restrain the reinstatement.
          </p>
        </section>

        <section id="repeat" className="legal-section">
          <h2 className="legal-h2">Repeat infringers</h2>
          <p className="legal-p">
            Verse maintains a policy of terminating the accounts of users who are found to be repeat infringers
            in appropriate circumstances. A user who receives multiple valid DMCA takedowns may have their
            account suspended or permanently terminated, at Verse's sole discretion.
          </p>
        </section>

        <section id="good-faith" className="legal-section">
          <h2 className="legal-h2">Good-faith obligations</h2>
          <p className="legal-p">
            Under 17 U.S.C. § 512(f), any person who knowingly materially misrepresents that material is
            infringing, or that material was removed by mistake or misidentification, is liable for damages —
            including costs and attorneys' fees. Please ensure your notice or counter-notice is submitted in
            good faith and based on accurate information.
          </p>
        </section>

        <section id="fair-use" className="legal-section">
          <h2 className="legal-h2">Fair use &amp; fan works</h2>
          <p className="legal-p">
            Verse is a creative-writing community built on fan creativity. Many works on the platform constitute
            transformative fan fiction that may qualify as fair use under 17 U.S.C. § 107. Before submitting a
            takedown notice, please consider whether the work is:
          </p>
          <ul className="legal-ul">
            <li>Transformative in nature (adding new meaning, expression, or message)</li>
            <li>Non-commercial and posted without financial gain</li>
            <li>Using only the amount of the original work necessary for the transformative purpose</li>
            <li>Not a market substitute for the original work</li>
          </ul>
          <p className="legal-p">
            We encourage rights holders to engage with fan communities constructively. Many copyright owners
            maintain official fan-work policies. Submitting takedowns against clearly transformative fan works
            may constitute misrepresentation under § 512(f).
          </p>
        </section>

        <section id="contact" className="legal-section">
          <h2 className="legal-h2">Contact the DMCA agent</h2>
          <div className="legal-contact-card">
            <div className="legal-contact-card__row">
              <span className="legal-contact-card__label">Designated Agent</span>
              <span>Zan Husain</span>
            </div>
            <div className="legal-contact-card__row">
              <span className="legal-contact-card__label">Mailing Address</span>
              <span>PO BOX 1101, CORAOPOLIS, PA 15108</span>
            </div>
            <div className="legal-contact-card__row">
              <span className="legal-contact-card__label">DMCA notices</span>
              <a href="mailto:support@versesite.com" className="legal-link">support@versesite.com</a>
            </div>
            <div className="legal-contact-card__row">
              <span className="legal-contact-card__label">General legal</span>
              <a href="mailto:support@versesite.com" className="legal-link">support@versesite.com</a>
            </div>
            <div className="legal-contact-card__row">
              <span className="legal-contact-card__label">In-platform</span>
              <span>Use the <strong>Report</strong> button on any story page</span>
            </div>
          </div>
          <p className="legal-p">
            For general questions about copyright, see our{' '}
            <Link to="/terms" className="legal-link">Terms of Service</Link>. For questions about how we
            handle your data in connection with a DMCA notice, see our{' '}
            <Link to="/privacy" className="legal-link">Privacy Policy</Link>.
          </p>
        </section>
      </div>
    </div>
  )
}
